import {
  useCallback, useEffect, useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import type { SpreadsheetColumnId } from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import type { BoundaryGroupId } from '../boundaries/boundaryIdentifier.type';
import {
  type Chart, ChartMetricType,
  isChartBoundaryStatistics,
  isChartMetricDemographic,
  isChartMetricSpreadsheetColumn,
} from '../mapSettings/data/charts/mapSettingsCharts.types';
import { addBoundaryStatsItems } from './stats.actionCreators';
import {
  createEmptyBoundaryStatItemDemographic,
  createEmptyBoundaryStatItemSpreadsheetColumn,
} from './stats.factory';
import {
  findBoundaryGroupStatItemDemographic, findBoundaryGroupStatItemSpreadsheetColumn,
} from './stats.helpers';
import { useBoundaryGroupStatsSelector } from './stats.selectors';
import type { BoundaryStatsItem } from './stats.state';

export const useChartsStats = (charts: ReadonlyArray<Chart>) => {
  const dispatch = useDispatch();
  const boundaryGroupsStats = useBoundaryGroupStatsSelector();

  const getSpreadsheetColumnBoundaryStats = useCallback(({
    boundaryGroupId,
    spreadsheetColumnId,
  }: {
    boundaryGroupId: BoundaryGroupId;
    spreadsheetColumnId: SpreadsheetColumnId;
  }): { item: BoundaryStatsItem; isCached: boolean } => {
    const existingStatsItem = findBoundaryGroupStatItemSpreadsheetColumn(boundaryGroupsStats, {
      boundaryGroupId,
      type: ChartMetricType.SpreadsheetColumn,
      column: spreadsheetColumnId,
    });

    return {
      item: existingStatsItem || createEmptyBoundaryStatItemSpreadsheetColumn({
        boundaryGroupId,
        column: spreadsheetColumnId,
      }),
      isCached: !!existingStatsItem,
    };
  }, [boundaryGroupsStats]);

  const getDemographicBoundaryStats = useCallback(({
    boundaryGroupId,
    demographicId,
  }: {
    boundaryGroupId: BoundaryGroupId;
    demographicId: string;
  }): { item: BoundaryStatsItem; isCached: boolean } => {
    const existingStatsItem = findBoundaryGroupStatItemDemographic(boundaryGroupsStats, {
      boundaryGroupId,
      type: ChartMetricType.Demographic,
      demographicId,
    });

    return {
      item: existingStatsItem || createEmptyBoundaryStatItemDemographic({
        boundaryGroupId,
        demographicId,
      }),
      isCached: !!existingStatsItem,
    };
  }, [boundaryGroupsStats]);

  const chartsStats = useMemo(() => charts.map((chart) => {
    if (isChartBoundaryStatistics(chart)) {
      return chart.metrics.map((metric) => {
        if (isChartMetricSpreadsheetColumn(metric)) {
          return getSpreadsheetColumnBoundaryStats({
            boundaryGroupId: chart.boundaryGroupId,
            spreadsheetColumnId: metric.column,
          }).item;
        }
        if (isChartMetricDemographic(metric)) {
          return getDemographicBoundaryStats({
            boundaryGroupId: chart.boundaryGroupId,
            demographicId: metric.demographicId,
          }).item;
        }
        return null;
      });
    }
    return null;
  }), [charts, getDemographicBoundaryStats, getSpreadsheetColumnBoundaryStats]);

  useEffect(() => {
    const itemsToRequest: BoundaryStatsItem[] = [];

    charts.forEach((chart) => {
      if (isChartBoundaryStatistics(chart)) {
        chart.metrics.forEach((metric) => {
          let itemData = null;
          if (isChartMetricSpreadsheetColumn(metric)) {
            itemData = getSpreadsheetColumnBoundaryStats({
              boundaryGroupId: chart.boundaryGroupId,
              spreadsheetColumnId: metric.column,
            });
          }
          if (isChartMetricDemographic(metric)) {
            itemData = getDemographicBoundaryStats({
              boundaryGroupId: chart.boundaryGroupId,
              demographicId: metric.demographicId,
            });
          }
          if (itemData && !itemData.isCached) {
            itemsToRequest.push(itemData.item);
          }
        });
      }
    });

    dispatch(addBoundaryStatsItems(itemsToRequest));
  }, [charts, dispatch, getDemographicBoundaryStats, getSpreadsheetColumnBoundaryStats]);

  return {
    chartsStats,
  };
};
